import { useState } from "react"

// ** Third Party Components
import axios from "axios"
import { useQuery } from "@tanstack/react-query"

// ** Types
import { DictType } from "@pma/types"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Routes
import { MAPBOX_PLACES_ENDPOINT } from "@pma/utils/api-routes"

// ** Theme Components
import { Popover, PopoverContent, PopoverTrigger } from "@pma/components/common/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@pma/components/common/command"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

interface Props {
  setGeoLocation: any
  location: string
  disabled?: boolean
}

const GeoLocationAddress = ({ setGeoLocation, location, disabled }: Props) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale == "he"
  const MAP_BOX_TOKEN = process.env.REACT_APP_TOKEN

  const [isOpen, setIsOpen] = useState(false)
  const [searchInput, setSearchInput] = useState("")

  const getLocationListService = async () => {
    const response = await axios.get(
      `${MAPBOX_PLACES_ENDPOINT} + ${searchInput}.json?access_token=${MAP_BOX_TOKEN}`,
    )

    return response?.data
  }

  const { data, refetch } = useQuery({
    queryKey: ["geo-code"],
    queryFn: getLocationListService,
    enabled: searchInput.length > 3,
  })

  const refetchLocation = (location: string) => {
    setSearchInput(location)
    refetch()
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger disabled={disabled}>
        <div
          className={cn(
            "w-full cursor-pointer gap-2.5 rounded-lg border border-neutral-200 px-4 py-2",
            disabled ? "bg-neutral-50" : "bg-white",
          )}
        >
          <h4
            className={cn(
              "w-[28ch] overflow-hidden truncate font-[inter] text-sm font-normal",
              isHebrew ? "text-right" : "text-left",
              disabled ? "text-neutral-400" : "text-neutral-800",
            )}
          >
            {location ? location : translate("to add address turn on the GIS toggle")}
          </h4>
        </div>
      </PopoverTrigger>

      <PopoverContent className='p-0'>
        <Command>
          <CommandInput
            placeholder={translate("Search locations")}
            onChangeCapture={(e: any) => refetchLocation(e?.target?.value)}
          />
          <CommandList>
            <CommandEmpty>{translate("No locations match")}</CommandEmpty>
            <CommandGroup>
              {data?.features?.map((location: DictType) => (
                <CommandItem
                  key={location.id}
                  value={location.text}
                  onSelect={() => {
                    setGeoLocation(location.place_name)
                    setIsOpen(false)
                  }}
                  className={cn("my-1", isHebrew ? "rtl" : "ltr")}
                >
                  {location.place_name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default GeoLocationAddress
